import { AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { Courier, CourierErrorKey, TranslationKey } from '@dukanti/shared';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

export interface DeleteCourierDialogProps {
  readonly courier: Courier | null;
  readonly open: boolean;
  readonly isDeleting?: boolean;
  readonly errorKey?: CourierErrorKey | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: () => void;
}

export function DeleteCourierDialog({
  courier,
  open,
  isDeleting = false,
  errorKey,
  onOpenChange,
  onConfirm,
}: DeleteCourierDialogProps): React.JSX.Element {
  const { t, dir } = useTranslation();

  return (
    <AlertDialog open={open} onOpenChange={(next) => !isDeleting && onOpenChange(next)}>
      <AlertDialogContent dir={dir} data-testid="delete-courier-dialog">
        <div className="bg-terracotta/10 text-terracotta mx-auto mb-2 flex size-12 items-center justify-center rounded-full">
          <Trash2 className="size-6" aria-hidden />
        </div>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center">
            {t('admin.couriers.delete.title' as TranslationKey)}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {t('admin.couriers.delete.description' as TranslationKey, {
              name: courier?.name ?? '',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {errorKey ? (
          <div
            role="alert"
            className="border-terracotta/30 bg-terracotta/10 text-sand-700 rounded-card flex items-start gap-2 border p-3 text-start text-sm"
          >
            <AlertCircle className="text-terracotta mt-0.5 size-4 shrink-0" aria-hidden />
            <p>{t(errorKey as TranslationKey)}</p>
          </div>
        ) : null}

        <AlertDialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={isDeleting}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isDeleting || !courier}
            onClick={onConfirm}
            aria-busy={isDeleting || undefined}
          >
            {isDeleting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            <span>{t('admin.couriers.actions.delete' as TranslationKey)}</span>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
